import { useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { Layer, Source } from 'react-map-gl';
import type { Expression } from 'mapbox-gl';
import { wellsLayerVisibilityAtom } from './atoms';

const GEO_ATTRIBUTES = [
  'survey',
  'stick',
  'surface_hole',
  'bottom_hole',
] as const;

const DEFAULT_COLOR = '#0F62FE';
const HIGHLIGHT_COLOR = '#FF7F00';

interface WellsLayerProps {
  workspaceId: number;
  filterParams: string;
  highlightedUwis?: Set<string>;
}

export const WellsLayer = ({
  workspaceId,
  filterParams,
  highlightedUwis,
}: WellsLayerProps) => {
  const isVisible = useAtomValue(wellsLayerVisibilityAtom);

  const tilesByGeo = useMemo(() => {
    const base = `${window.location.origin}/maps/tiles/dca_workspace_wells`;
    const qs = new URLSearchParams({ dca_workspace_id: String(workspaceId) });
    if (filterParams) {
      for (const [k, v] of new URLSearchParams(filterParams)) {
        qs.set(k, v);
      }
    }
    const suffix = qs.toString();

    return Object.fromEntries(
      GEO_ATTRIBUTES.map((attr) => [
        attr,
        [`${base}/${attr}/{z}/{x}/{y}.mvt?${suffix}`],
      ])
    ) as Record<(typeof GEO_ATTRIBUTES)[number], string[]>;
  }, [workspaceId, filterParams]);

  const hasHighlight = highlightedUwis && highlightedUwis.size > 0;
  const uwiList = useMemo(
    () => (hasHighlight ? Array.from(highlightedUwis) : []),
    [hasHighlight, highlightedUwis]
  );

  const isHighlighted: Expression | false = hasHighlight
    ? ['in', ['get', 'uwi'], ['literal', uwiList]]
    : false;

  const color = isHighlighted
    ? (['case', isHighlighted, HIGHLIGHT_COLOR, DEFAULT_COLOR] as Expression)
    : DEFAULT_COLOR;

  if (!isVisible) return null;

  return (
    <>
      <Source id="dca-wells-survey" type="vector" tiles={tilesByGeo.survey}>
        <Layer
          id="dca-wells-survey-lines"
          type="line"
          source-layer="hits"
          filter={['all']}
          paint={{
            'line-color': color,
            'line-width': 4,
            'line-opacity': 0.8,
          }}
        />
      </Source>

      <Source id="dca-wells-stick" type="vector" tiles={tilesByGeo.stick}>
        <Layer
          id="dca-wells-stick-lines"
          type="line"
          source-layer="hits"
          filter={['all', ['!', ['has', 'survey']]]}
          paint={{
            'line-color': color,
            'line-width': 4,
            'line-opacity': 0.6,
            'line-dasharray': [1, 1],
          }}
        />
      </Source>

      <Source
        id="dca-wells-surface-hole"
        type="vector"
        tiles={tilesByGeo.surface_hole}
      >
        <Layer
          id="dca-wells-surface-hole-points"
          type="circle"
          source-layer="hits"
          filter={['all']}
          paint={{
            'circle-color': color,
            'circle-radius': 4,
            'circle-opacity': 0.8,
          }}
        />
      </Source>

      <Source
        id="dca-wells-bottom-hole"
        type="vector"
        tiles={tilesByGeo.bottom_hole}
      >
        <Layer
          id="dca-wells-bottom-hole-points"
          type="circle"
          source-layer="hits"
          filter={['all']}
          paint={{
            'circle-color': color,
            'circle-radius': 4,
            'circle-opacity': 0.8,
          }}
        />
      </Source>
    </>
  );
};
